import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Tabs,
  Tab,
  Divider,
  useTheme,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import GarageIcon from '@mui/icons-material/Garage';
import LandscapeIcon from '@mui/icons-material/Landscape';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Etage } from '../types';
import { cacheService } from '../../functions/services/cacheService';

interface Floor {
  id: string;
  name: string;
  icon: React.ComponentType;
  isMain?: boolean;
}

interface FloorSelectorProps {
  selectedFloor: string;
  onFloorChange: (floorId: string) => void;
}

// Map of floor types to their icons
const FLOOR_ICONS: Record<string, React.ComponentType> = {
  'Rez-de-chaussée': HomeIcon,
  'Étage': BusinessIcon,
  'Sous-sol': LayersIcon,
  'Garage': GarageIcon,
  'Extérieur': LandscapeIcon,
  'Électrique': ElectricBoltIcon
};

// Helper function to determine if a floor is the main floor
const isMainFloor = (name: string | undefined): boolean => {
  if (!name) return false;
  return name.toLowerCase().includes('rez-de-chaussée');
};

const FloorSelector: React.FC<FloorSelectorProps> = ({ 
  selectedFloor, 
  onFloorChange
}) => {
  const theme = useTheme();
  const [floors, setFloors] = useState<Floor[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTabs, setActiveTabs] = useState<string[]>([]);
  const [availableOptions, setAvailableOptions] = useState<Floor[]>([]);

  useEffect(() => {
    const loadFloors = () => {
      const cachedEtages = cacheService.getEtages();
      if (cachedEtages && cachedEtages.length > 0) {
        // Convert all etages to floor objects
        const allFloors = cachedEtages.map(etage => ({
          id: etage.id,
          name: etage.name,
          icon: FLOOR_ICONS[etage.name] || HomeIcon,
          isMain: isMainFloor(etage.name)
        }));

        // Set all floors as active tabs
        setFloors(allFloors);
        setActiveTabs(allFloors.map(f => f.id));
        setAvailableOptions([]); // No available options since all floors are active
        
        setLoading(false);
      }
    };

    loadFloors();
  }, [selectedFloor, onFloorChange]);

  const handleFloorChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue) {
      onFloorChange(newValue);
    }
  };

  const getFloorInfo = (floorId: string) => {
    return floors.find(f => f.id === floorId) || availableOptions.find(o => o.id === floorId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no floors are loaded, show a message
  if (floors.length === 0 && availableOptions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Aucun étage disponible</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      mb: 4
    }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'primary.main',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Sélectionner l'étage
            </Typography>
          </Box>
          
          <Box sx={{ 
            backgroundColor: theme.palette.grey[50],
            borderRadius: 2,
            p: 1,
            boxShadow: theme.shadows[1]
          }}>
            <Tabs
              value={selectedFloor || ''}
              onChange={handleFloorChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="floor selection tabs"
              sx={{
                '& .MuiTab-root': {
                  minWidth: 180,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  px: 3,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none'
                }
              }}
            >
              {floors.map(floor => {
                const Icon = floor.icon;
                return (
                  <Tab
                    key={floor.id}
                    value={floor.id}
                    label={
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        py: 0.5
                      }}>
                        <Icon />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{floor.name}</Typography>
                      </Box>
                    }
                    id={`floor-tab-${floor.id}`}
                    aria-controls={`floor-tabpanel-${floor.id}`}
                  />
                );
              })}
            </Tabs>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default FloorSelector; 